<template>
  <div
    :class="[
      outerClass,
      ns.b(),
      ns.m(inputNumberSize),
      ns.is('disabled', inputNumberDisabled as boolean),
      ns.is('without-controls', !controls),
      ns.is('controls-right', controlsAtRight),
    ]"
    @dragstart.prevent
  >
    <span
      v-if="controls"
      v-repeat-click="decrease"
      role="button"
      :class="[ns.e('decrease'), ns.is('disabled', minDisabled)]"
      @keydown.enter="decrease"
    >
      <sg-icon>
        <CaretBottomSolid v-if="controlsAtRight" />
        <Minus v-else />
      </sg-icon>
    </span>
    <span
      v-if="controls"
      v-repeat-click="increase"
      role="button"
      :class="[ns.e('increase'), ns.is('disabled', maxDisabled)]"
      @keydown.enter="increase"
    >
      <sg-icon>
        <CaretTopSolid v-if="controlsAtRight" />
        <Plus v-else />
      </sg-icon>
    </span>
    <sg-input
      ref="input"
      type="number"
      :step="step"
      v-bind="attrs"
      :model-value="displayValue"
      :disabled="(inputNumberDisabled as boolean)"
      :size="inputNumberSize"
      :validate-event="false"
      @keydown.up.prevent="increase"
      @keydown.down.prevent="decrease"
      @on-input="handleInput"
      @on-blur="handleBlur"
      @on-focus="handleFocus"
      @on-change="handleInputChange"
    >
      <template v-if="!controls && closable" #suffix>
        <sg-icon
          class="sg-input__icon closable"
          @click="handleClearClick"
        >
          <Close />
        </sg-icon>
      </template>
    </sg-input>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch,
  onMounted,
  onUpdated,
} from 'vue'

import { SgIcon } from '@sgui-plus/components/icon'
import { RepeatClick } from '@sgui-plus/directives'
import {
  useDisabled,
  useSize,
  useNamespace,
  useFormItem
} from '@sgui-plus/hooks'
import SgInput from '@sgui-plus/components/input'
import { isNumber, isString, isUndefined } from '@sgui-plus/utils/util'
import { debugWarn } from '@sgui-plus/utils/error'
import { CaretTopSolid, CaretBottomSolid, Plus, Minus, Close } from '@sgui-plus/icons-vue'
import { inputNumberProps, inputNumberEmits } from './input-number'
import { isNil } from 'lodash'

import type { ComponentPublicInstance } from 'vue'

interface IData {
  currentValue: number | undefined | null
  userInput: null | number | string
}

export default defineComponent({
  name: 'SgInputNumber',
  components: {
    SgInput,
    SgIcon,
    CaretTopSolid, CaretBottomSolid, Plus, Minus, Close
  },
  directives: {
    RepeatClick,
  },
  props: inputNumberProps,
  emits: inputNumberEmits,
  setup(props, { emit, attrs }) {
    const { formItem } = useFormItem()
    const input = ref<ComponentPublicInstance<typeof SgInput>>()
    const data = reactive<IData>({
      currentValue: props.modelValue,
      userInput: null,
    })
    const ns = useNamespace('input-number')
    const outerClass = props.class
    const minDisabled = computed(() => _decrease(props.modelValue as number)! < (attrs.min as number))
    const maxDisabled = computed(() => _increase(props.modelValue as number)! > (attrs.max as number))

    const numPrecision = computed(() => {
      const stepPrecision = getPrecision(props.step)
      if (props.precision !== undefined) {
        if (stepPrecision > props.precision) {
          debugWarn(
            'InputNumber',
            'precision should not be less than the decimal places of step'
          )
        }
        return props.precision
      } else {
        return Math.max(getPrecision(props.modelValue), stepPrecision)
      }
    })
    const controlsAtRight = computed(() => {
      return props.controls && props.controlsPosition === 'right'
    })

    const inputNumberSize = useSize()
    const inputNumberDisabled = useDisabled()

    const displayValue = computed(() => {
      if (data.userInput !== null) {
        return data.userInput
      }
      let currentValue: number | string | undefined | null = data.currentValue
      if (isNil(currentValue)) return ''
      if (isNumber(currentValue)) {
        if (Number.isNaN(currentValue)) return ''
        if (props.precision !== undefined) {
          currentValue = currentValue.toFixed(props.precision)
        }
      }
      return currentValue
    })
    const toPrecision = (num: number, pre?: number) => {
      if (pre === undefined) pre = numPrecision.value
      return parseFloat(
        `${Math.round(num * Math.pow(10, pre)) / Math.pow(10, pre)}`
      )
    }
    const getPrecision = (value: number | undefined) => {
      if (value === undefined) return 0
      const valueString = value.toString()
      const dotPosition = valueString.indexOf('.')
      let precision = 0
      if (dotPosition !== -1) {
        precision = valueString.length - dotPosition - 1
      }
      return precision
    }
    const _increase = (val: number) => {
      if (!isNumber(val)) return data.currentValue
      const precisionFactor = Math.pow(10, numPrecision.value)
      // Solve the accuracy problem of JS decimal calculation by converting the value to integer.
      val = isNumber(val) ? val : NaN
      return toPrecision(
        (precisionFactor * val + precisionFactor * props.step) / precisionFactor
      )
    }
    const _decrease = (val: number) => {
      if (!isNumber(val)) return data.currentValue
      const precisionFactor = Math.pow(10, numPrecision.value)
      // Solve the accuracy problem of JS decimal calculation by converting the value to integer.
      val = isNumber(val) ? val : NaN
      return toPrecision(
        (precisionFactor * val - precisionFactor * props.step) / precisionFactor
      )
    }
    const increase = () => {
      if (inputNumberDisabled.value || maxDisabled.value) return
      const value = props.modelValue || 0
      const newVal = _increase(value)
      setCurrentValue(newVal as number, 'increase')
    }
    const decrease = () => {
      if (inputNumberDisabled.value || minDisabled.value) return
      const value = props.modelValue || 0
      const newVal = _decrease(value)
      setCurrentValue(newVal as number, 'decrease')
    }
    const verifyValue = (
      value: number | string | null | undefined,
      update?: boolean
    ): number | null | undefined => {
      const { step, precision, stepStrictly, valueOnClear } = props
      const  { min, max } = attrs
      let newVal = Number(value)
      if (isNil(value) || Number.isNaN(newVal)) {
        return null
      }
      if (value === '') {
        if (valueOnClear === null) {
          return null
        }
        newVal = isString(valueOnClear) ? { min, max }[valueOnClear] : valueOnClear
      }
      if (stepStrictly) {
        newVal = Math.round(newVal / step) * step
      }
      if (!isUndefined(precision)) {
        newVal = toPrecision(newVal, precision)
      }
      if (newVal > (attrs.max as number) || newVal < (attrs.min as number)) {
        newVal = newVal > (attrs.max as number) ? (attrs.max as number) : (attrs.min as number)
        update && emit('update:modelValue', newVal)
      }
      return newVal
    }
    const setCurrentValue = (val: number | string | undefined | null, mode?: string) => {
      const oldVal = data.currentValue
      const newVal = verifyValue(val)
      if (oldVal === newVal) return
      data.userInput = null
      emit('update:modelValue', newVal!)
      emit('onInput', newVal!)
      emit('onChange', newVal!, oldVal!)
      if(mode == 'decrease'){
        emit('onDown', newVal!, oldVal!)
      }
      if(mode == 'increase'){
        emit('onUp', newVal!, oldVal!)
      }
      if (props.validateEvent) {
        formItem?.validate?.('change').catch((err) => debugWarn(err))
      }
      data.currentValue = newVal
    }
    const handleInput = (value: string) => {
      return (data.userInput = value)
    }
    const handleInputChange = (value: string) => {
      const newVal = value !== '' ? Number(value) : ''
      if ((isNumber(newVal) && !Number.isNaN(newVal)) || value === '') {
        setCurrentValue(newVal)
      }
      data.userInput = null
    }

    const focus = () => {
      input.value?.focus?.()
    }

    const blur = () => {
      input.value?.blur?.()
    }

    const select = () => {
      input.value?.select?.()
    }
    const handleFocus = (event: FocusEvent) => {
      emit('onFocus', event)
    }

    const handleBlur = (event: FocusEvent) => {
      emit('onBlur', event)
      if (props.validateEvent) {
        formItem?.validate?.('blur').catch((err) => debugWarn(err))
      }
    }
    const handleClearClick = () => {
      setCurrentValue('')
      emit('onClear')
    }

    watch(
      () => props.modelValue,
      (value) => {
        data.currentValue = verifyValue(value, true)
        data.userInput = null
      },
      { immediate: true }
    )
    onMounted(() => {
      const innerInput = input.value?.input as HTMLInputElement
      innerInput.setAttribute('role', 'spinbutton')
      innerInput.setAttribute('aria-valuemax', String((attrs.max as number)))
      innerInput.setAttribute('aria-valuemin', String((attrs.min as number)))
      innerInput.setAttribute('aria-valuenow', String(data.currentValue))
      innerInput.setAttribute(
        'aria-disabled',
        String(inputNumberDisabled.value)
      )
      if (!isNumber(props.modelValue)) {
        let val: number | undefined = Number(props.modelValue)
        if (isNaN(val)) {
          val = undefined
        }
        emit('update:modelValue', val)
      }
    })
    onUpdated(() => {
      const innerInput = input.value?.input
      innerInput.setAttribute('aria-valuenow', data.currentValue)
    })
    return {
      outerClass,
      attrs,
      input,
      displayValue,
      handleInput,
      handleInputChange,
      controlsAtRight,
      decrease,
      increase,
      inputNumberSize,
      inputNumberDisabled,
      maxDisabled,
      minDisabled,
      focus,
      blur,
      select,
      handleFocus,
      handleBlur,
      handleClearClick,
      ns,
    }
  },
})
</script>
